import { log2 } from "../../utils/log2";
import { getPersistenceData, removePersistenceData } from "../../utils/persistenceStorage";

const log = log2("chat.redux");
const state = {
    connected: false,
    connectionLost: false,
    mouseUpOnWindow: false,
    connectionCount: 0,
    lastConversationId: getPersistenceData("conversationId") || null,
    socketIsOpen: false,
    visitorIsLogging: false,
    visitorIsLoggedIn: false,
    chatAgents: [],
    queueNumber: undefined,
    conversationId: getPersistenceData("conversationId") || null,
    visitorId: getPersistenceData("visitorId", null) || null,
    preDefinedUserData: {
        initialized: false,
    },
    preSelectedDepartment: null,
    isChatbotActive: false,
    autoTranslate: false,
    visitorInfo: {},
    preferredChatbotStartBlockId: new URLSearchParams(window.location.search).get("chatbot_start_block_id"),
    contactPropertyData: {},
    skipLoginFormFromTrigger: false,
};

const getters = {
    isConnected: (state) => {
        return state.connected;
    },
    mouseUpOnWindow: (state) => {
        return state.mouseUpOnWindow;
    },
    reconnecting: (state) => {
        return state.connectionCount >= 1 && state.connectionLost;
    },
    connectingSocket: (state, getters, rootState) => {
        return !(rootState.site.site != null && rootState.site.site._id != null);
    },
    connectingRepresentative: (state, getters, rootState) => {
        const anyAgentConnected = state.chatAgents.filter((x) => x._id !== "chatbot").length !== 0;
        const isChatbotActive = state.isChatbotActive;
        log("connectingRepresentative", {
            anyAgentConnected,
            isChatbotActive,
            visitorIsLoggedIn: state.visitorIsLoggedIn,
            showAfterActions: rootState.app.showAfterActions,
        });
        return state.visitorIsLoggedIn && !rootState.app.showAfterActions && !anyAgentConnected && !isChatbotActive;
    },

    connectionLost: (state) => {
        return state.connectionLost;
    },
};

const mutations = {
    ["UPDATE_USER_LANG"](state, payload) {
        log("UPDATE_USER_LANG", payload);
        state.chatAgents = state.chatAgents.map((x) => {
            if (x._id === payload.userId) {
                x.lang = payload.lang;
            }
            return x;
        });
    },
    ["SET_VISITOR"](state, visitorId) {
        state.visitorId = visitorId;
    },
    ["SET_CONVERSATION"](state, conversationId) {
        state.conversationId = conversationId;
        if (!conversationId) {
            log("conversationId removing from SET_CONVERSATION");
            removePersistenceData("conversationId");
            state.lastConversationId = null;
        }
    },
    ["SET_CONNECTION_STATUS"](state, isDisconnected) {
        state.connectionLost = isDisconnected;
        state.socketIsOpen = !isDisconnected;
        if (!isDisconnected) {
            state.connectionCount += 1;
        }
    },
    ["SET_MOUSE_UP_ON_WINDOW"](state, payload) {
        state.mouseUpOnWindow = true;
    },
    ["CONNECTED"](state, conversationId) {
        state.connected = true;
        state.conversationId = conversationId;
        state.lastConversationId = conversationId;
    },
    ["SET_VISITOR_IS_LOGGING"](state, visitorIsLogging) {
        state.visitorIsLogging = visitorIsLogging;
    },
    ["SET_VISITOR_IS_LOGGED_IN"](state, visitorIsLoggedIn) {
        state.visitorIsLoggedIn = visitorIsLoggedIn;
    },
    ["SET_QUEUE_NUMBER"](state, queueNumber) {
        state.queueNumber = state.chatAgents.length ? undefined : queueNumber;
    },
    ["SET_CHAT_AGENTS"](state, agents) {
        state.chatAgents = agents;
    },
    ["UPDATE_CHAT_AGENTS"](state, userId) {
        state.chatAgents = state.chatAgents.filter((x) => x._id !== userId);
    },
    ["SET_VISITOR_ID"](state, id) {
        state.visitorId = id;
    },
    ["DISCONNECTED"](state) {
        state.connected = false;
        state.conversationId = null;
    },
    ["SET_CHATBOT_STATE"](state, isActive) {
        state.isChatbotActive = isActive;
    },
    ["SET_PRE_DEFINED_USER_DATA"](state, payload) {
        log("SET_PRE_DEFINED_USER_DATA", payload);
        if (payload) {
            state.preDefinedUserData = {
                ...state.preDefinedUserData,
                ...payload,
                initialized: true,
            };
            log("SET_PRE_DEFINED_USER_DATA end", state.preDefinedUserData);
        }
    },
    ["CHANGE_PRE_SELECTED_DEPARTMENT"](state, department) {
        log("CHANGE_PRE_SELECTED_DEPARTMENT", department);
        if (department) {
            state.preSelectedDepartment = department;
        }
    },
    ["SET_AUTO_TRANSLATE"](state, autoTranslate) {
        state.autoTranslate = autoTranslate;
    },
    ["SET_VISITOR_INFO"](state, visitorInfo) {
        state.visitorInfo = visitorInfo;
    },
    ["SET_PREFERRED_CHATBOT_START_BLOCK_ID"](state, blockId) {
        state.preferredChatbotStartBlockId = blockId;
    },
    ["SET_CONTACT_PROPERTY_DATA"](state, payload) {
        log("SET_CONTACT_PROPERTY_DATA", payload);
        state.contactPropertyData = {
            ...state.contactPropertyData,
            ...payload,
        };
    },
    ["SET_SKIP_LOGIN_FORM_FROM_TRIGGER"](state, payload) {
        state.skipLoginFormFromTrigger = !!payload;
    },
};

const actions = {
    connectChat({ commit }, conversationId) {
        log("connectChat", conversationId);
        commit("CONNECTED", conversationId);
    },
    resetConversation({ commit }, payload) {
        log("redux action: resetConversation", payload);
        commit("SET_CONVERSATION", null);
    },
    disconnect({ commit }) {
        log("redux action: disconnect");
        commit("DISCONNECTED");
    },
    setVisitor({ commit }, payload) {
        log("redux action: setVisitor", payload);
        commit("SET_VISITOR", payload);
    },
    setConnectionStatus({ commit }, payload) {
        log("redux action: setConnectionStatus", payload);
        commit("SET_CONNECTION_STATUS", payload);
    },
    setMouseUp({ commit }, payload) {
        log("redux action: setMouseUp", payload);
        commit("SET_MOUSE_UP_ON_WINDOW", payload);
    },
    setVisitorIsLogging({ commit }, visitorIsLogging) {
        log("setVisitorIsLogging", visitorIsLogging);
        commit("SET_VISITOR_IS_LOGGING", visitorIsLogging);
        if (visitorIsLogging) commit("SET_VISITOR_IS_LOGGED_IN", false);
    },
    setVisitorIsLoggedIn({ commit }, visitorIsLoggedIn) {
        log("setVisitorIsLoggedIn", visitorIsLoggedIn);
        commit("SET_VISITOR_IS_LOGGED_IN", visitorIsLoggedIn);
        if (visitorIsLoggedIn) commit("SET_VISITOR_IS_LOGGING", false);
    },
    setQueueNumber({ commit }, queueNumber) {
        log("setQueueNumber", queueNumber);
        commit("SET_QUEUE_NUMBER", queueNumber);
    },
    setChatAgents({ commit }, agents) {
        log("setChatAgents", agents);
        commit("SET_CHAT_AGENTS", agents);
    },
    setVisitorId({ commit }, id) {
        log("setVisitorId", id);
        commit("SET_VISITOR_ID", id);
    },
    setPreDefinedUserData({ commit }, payload) {
        log("setPreDefinedUserData", payload);
        commit("SET_PRE_DEFINED_USER_DATA", payload);
    },
    changePreSelectedDepartment({ commit }, department) {
        log("changePreSelectedDepartment", department);
        commit("CHANGE_PRE_SELECTED_DEPARTMENT", department);
    },
    setChatbotActiveState({ commit }, isActive) {
        log("setChatbotActiveState", isActive);
        commit("SET_CHATBOT_STATE", isActive);
    },
    setPreferredChatbotStartBlockId({ commit }, payload) {
        log("setPreferredChatbotStartBlockId", payload);
        commit("SET_PREFERRED_CHATBOT_START_BLOCK_ID", payload);
    },
    setContactPropertyData({ commit, state }, payload) {
        log("setContactPropertyData", payload);
        commit("SET_CONTACT_PROPERTY_DATA", payload);
    },
    setSkipLoginFormFromTrigger({ commit }, payload) {
        commit("SET_SKIP_LOGIN_FORM_FROM_TRIGGER", payload);
    },
};

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};



// WEBPACK FOOTER //
// ./src/store/modules/chat.js